import { type JSX, useCallback, useEffect, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import {
  FAELLIGKEITSNAME,
  PRIORITAETSNAME,
  VERLAUFSNAME,
  meineWiedervorlagen,
  wiedervorlage as ladeWiedervorlage,
  wiedervorlageAendern,
  wiedervorlageAnlegen,
  wiedervorlageErledigen,
  wiedervorlagenAmKunden,
  wiedervorlageWiedereroeffnen,
  zustaendige as ladeZustaendige,
  type Faelligkeit,
  type Listenstand,
  type Prioritaet,
  type Verlaufseintrag,
  type Wiedervorlage,
  type Zustaendigenauswahl,
  type Zustaendigwunsch,
} from '../api/wiedervorlagen';
import { alsTag, alsZeitpunkt, heute } from '../datum';
import { markenklasse, type Markenart } from '../marken';
import { Abschlussreihe } from '../komponenten/Abschlussreihe';
import { Verlauf, type Verlaufseintrag as Akteneintrag } from '../komponenten/Akte';
import { Dialog } from '../komponenten/Dialog';
import { Feld } from '../komponenten/Feld';
import { Liste } from '../komponenten/Liste';
import { Meldung } from '../komponenten/Meldung';
import { Segmente } from '../komponenten/Segmente';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';
import { KundeVerweis } from '../komponenten/Kontaktkarte';

/**
 * WIEDERVORLAGEN (KUN-15): Fälligkeit, Zuständiger, Priorität und Erinnerung;
 * die Übersicht „Meine Wiedervorlagen".
 *
 * Drei Orte, ein Fenster:
 *
 *   * „Meine Wiedervorlagen" — was der angemeldeten Person oder einer ihrer
 *     Rollen zugewiesen ist, Vorgabe „fällig" (heute fällig, überfällig oder
 *     mit erreichter Erinnerung).
 *   * Die Kundenakte — in der Aktivitätsspalte die offenen dieses Kunden.
 *   * Das Wiedervorlagenfenster — ansehen, ändern, erledigen, wieder öffnen,
 *     samt Verlauf.
 *
 * KEINE RÜCKFRAGE (Tabelle 29, ÄA-62 B): Nichts hier ist unumkehrbar.
 * Erledigen lässt sich wieder öffnen, und gelöscht wird eine Wiedervorlage
 * nur mit ihrem Kunden.
 *
 * TELEFON (AeA-63): einspaltig ab 360 px — die Tabelle rollt in ihrer Hülle,
 * das Fenster stapelt die Felder. Kundendaten sind kein Planen und keine
 * Verwaltung; bearbeitet werden darf deshalb auch dort.
 */

/** Die Fälligkeit als Marke — was überfällig ist, fällt auf. */
const FAELLIGKEITSMARKE: Readonly<Record<Faelligkeit, Markenart>> = {
  ueberfaellig: 'sackgasse',
  faellig: 'aufmerksamkeit',
  erinnert: 'aufmerksamkeit',
  offen: 'laufend',
  erledigt: 'abschluss',
};

function faelligkeitsmarke(w: Wiedervorlage): JSX.Element {
  return <span className={markenklasse(FAELLIGKEITSMARKE[w.faelligkeit])}>{FAELLIGKEITSNAME[w.faelligkeit]}</span>;
}

function zustaendigName(w: Wiedervorlage): string {
  return w.zustaendig.art === 'person' ? w.zustaendig.name : `Rolle ${w.zustaendig.bezeichnung}`;
}

function bezugName(w: Wiedervorlage): string | null {
  if (w.bezug === null) return null;
  const art = w.bezug.art === 'angebot' ? 'Angebot' : 'Auftrag';
  return w.bezug.nummer === null ? `${art} (Entwurf)` : `${art} ${w.bezug.nummer}`;
}

/*
 * DIE ERINNERUNG IST EIN ZEITPUNKT IN BERLIN (FRS-01). Das Eingabefeld
 * `datetime-local` kennt keine Zone; die API verlangt eine. Umgerechnet wird
 * mit dem Versatz der maßgeblichen Zone, nicht dem des Browsers.
 */
function berlinerVersatzMinuten(zeit: Date): number {
  const teil = new Intl.DateTimeFormat('en-US', { timeZone: 'Europe/Berlin', timeZoneName: 'shortOffset' })
    .formatToParts(zeit)
    .find((t) => t.type === 'timeZoneName')?.value;
  const treffer = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(teil ?? '');
  if (treffer === null) return 0;
  const minuten = Number(treffer[2]) * 60 + Number(treffer[3] ?? '0');
  return treffer[1] === '-' ? -minuten : minuten;
}

export function erinnerungAlsIso(lokal: string): string | null {
  if (lokal.trim() === '') return null;
  const alsUtc = new Date(`${lokal}:00Z`);
  if (Number.isNaN(alsUtc.getTime())) return null;
  return new Date(alsUtc.getTime() - berlinerVersatzMinuten(alsUtc) * 60_000).toISOString();
}

export function erinnerungAlsEingabe(iso: string | null): string {
  if (iso === null) return '';
  const teile = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
  return teile.replace(' ', 'T');
}

function wunschAlsWert(z: Zustaendigwunsch): string {
  return z.art === 'person' ? `person:${z.id}` : `rolle:${z.kennung}`;
}

function wertAlsWunsch(wert: string): Zustaendigwunsch | null {
  const [art, ...rest] = wert.split(':');
  const schluessel = rest.join(':');
  if (schluessel === '') return null;
  if (art === 'person') return { art: 'person', id: schluessel };
  if (art === 'rolle') return { art: 'rolle', kennung: schluessel };
  return null;
}

/* ========================================================================== */
/* MEINE WIEDERVORLAGEN                                                        */
/* ========================================================================== */

export function MeineWiedervorlagen({
  ich,
  darfAendern,
  beiKunde,
  oeffnen,
}: {
  /** Auftrag CRM-170: die Wiedervorlage, die die Glocke gewählt hat — gleich geöffnet. */
  readonly oeffnen?: string | undefined;
  /** Die angemeldete Person — die Vorbelegung des Zuständigen. */
  readonly ich: string;
  /** `kunde/aendern`: ändern und neu zuweisen. Erledigen darf auch, wem sie zugewiesen ist. */
  readonly darfAendern: boolean;
  readonly beiKunde?: ((id: string) => void) | undefined;
}): JSX.Element {
  const [stand, setzeStand] = useState<Listenstand>('faellig');
  const [liste, setzeListe] = useState<readonly Wiedervorlage[] | null>(null);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [offen, setzeOffen] = useState<string | null>(oeffnen ?? null);

  const holen = useCallback(async () => {
    try {
      setzeListe(await meineWiedervorlagen(stand));
      setzeFehler(null);
    } catch (f) {
      setzeListe([]);
      if (!(f instanceof ApiFehler)) throw f;
      setzeFehler(f);
    }
  }, [stand]);

  useEffect(() => {
    void holen();
  }, [holen]);

  useZaehler(liste === null ? undefined : zaehlwort(liste.length, 'Wiedervorlage', 'Wiedervorlagen'));

  return (
    <>
      {fehler !== null && <Meldung fehler={fehler} />}
      <Liste
        filter={
          <Segmente
            beschriftung="Stand"
            gewaehlt={stand}
            beiWahl={setzeStand}
            optionen={[
              { wert: 'faellig', text: 'fällig' },
              { wert: 'ueberfaellig', text: 'überfällig' },
              { wert: 'offen', text: 'alle offenen' },
              { wert: 'erledigt', text: 'erledigt' },
            ]}
          />
        }
        hinweise={
          <p className="hilfe">
            Was Ihnen oder einer Ihrer Rollen zugewiesen ist. Fällig ist, was heute oder früher fällig
            ist oder dessen Erinnerung erreicht ist. Erledigte stehen 90 Tage in der Liste.
          </p>
        }
      >
        {liste === null && <p role="status">Wird geladen …</p>}
        {liste !== null && liste.length === 0 && (
          <p className="liste__leer">
            {stand === 'erledigt' ? 'In den letzten 90 Tagen nichts erledigt.' : 'Keine Wiedervorlage in dieser Auswahl.'}
          </p>
        )}
        {liste !== null && liste.length > 0 && (
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle" aria-label="Meine Wiedervorlagen">
              <thead>
                <tr>
                  <th scope="col">Fällig</th>
                  <th scope="col">Betreff</th>
                  <th scope="col">Kunde</th>
                  <th scope="col">Zuständig</th>
                  <th scope="col">Stand</th>
                </tr>
              </thead>
              <tbody>
                {liste.map((w) => (
                  <tr key={w.id}>
                    <th scope="row">
                      <button
                        type="button"
                        className="liste__oeffnen"
                        onClick={() => {
                          setzeOffen(w.id);
                        }}
                      >
                        <span className="liste__einzeilig">{alsTag(w.faelligAm)}</span>
                      </button>
                    </th>
                    <td>
                      <span className="liste__name">{w.betreff}</span>
                      {/* KUN-15: Priorität — nur wo sie vom Üblichen abweicht. */}
                      {w.prioritaet !== 2 && (
                        <>
                          <br />
                          <span className="hilfe">Priorität {PRIORITAETSNAME[w.prioritaet]}</span>
                        </>
                      )}
                    </td>
                    <td>
                      {w.kunde === null ? (
                        '—'
                      ) : beiKunde === undefined ? (
                        w.kunde.name
                      ) : (
                        /* Auftrag CRM-181: Kontaktkarte am Namen — beiKunde steht nur mit kunde/sehen. */
                        <KundeVerweis kundeId={w.kunde.id} zurAkte={beiKunde}>
                          <button
                            type="button"
                            className="knopf knopf--verweis"
                            onClick={() => {
                              if (w.kunde !== null) beiKunde(w.kunde.id);
                            }}
                          >
                            {w.kunde.name}
                          </button>
                        </KundeVerweis>
                      )}
                      {bezugName(w) !== null && (
                        <>
                          <br />
                          <span className="hilfe">{bezugName(w)}</span>
                        </>
                      )}
                    </td>
                    <td>{zustaendigName(w)}</td>
                    <td>{faelligkeitsmarke(w)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Liste>

      {offen !== null && (
        <Wiedervorlagefenster
          id={offen}
          ich={ich}
          darfAendern={darfAendern}
          beiSchliessen={() => {
            setzeOffen(null);
          }}
          beiAenderung={() => void holen()}
        />
      )}
    </>
  );
}

/* ========================================================================== */
/* IN DER KUNDENAKTE                                                           */
/* ========================================================================== */

/**
 * Die offenen Wiedervorlagen eines Kunden in der Aktivitätsspalte der Akte
 * (KUN-14 daneben), darunter „Wiedervorlage anlegen" mit `kunde/aendern`.
 */
export function AktenWiedervorlagen({
  kunde,
  ich,
  darfAendern,
}: {
  readonly kunde: string;
  readonly ich: string;
  readonly darfAendern: boolean;
}): JSX.Element {
  const [liste, setzeListe] = useState<readonly Wiedervorlage[] | null>(null);
  const [offen, setzeOffen] = useState<string | null>(null);
  const [neu, setzeNeu] = useState(false);

  const holen = useCallback(async () => {
    try {
      setzeListe(await wiedervorlagenAmKunden(kunde));
    } catch (f) {
      // Ohne Liste bleibt die Spalte leer; die Akte selbst meldet, was am Kunden scheitert.
      setzeListe([]);
      if (!(f instanceof ApiFehler)) throw f;
    }
  }, [kunde]);

  useEffect(() => {
    void holen();
  }, [holen]);

  const offene = (liste ?? []).filter((w) => w.erledigtAm === null);
  const eintraege: readonly Akteneintrag[] = offene.map((w) => ({
    kennung: w.id,
    art: w.faelligkeit === 'ueberfaellig' ? 'achtung' : 'vorgang',
    sinnbild: 'calendar-plus',
    titel: w.betreff,
    detail: `${FAELLIGKEITSNAME[w.faelligkeit]} · ${zustaendigName(w)}`,
    dringend: w.faelligkeit === 'ueberfaellig',
    datum: alsTag(w.faelligAm) ?? w.faelligAm,
    beiWahl: () => {
      setzeOffen(w.id);
    },
  }));

  return (
    <>
      <Verlauf
        titel="Wiedervorlagen"
        zusatz={`${String(offene.length)} offen`}
        eintraege={eintraege}
        leer={liste === null ? 'Wird geladen …' : 'Keine offene Wiedervorlage.'}
      />
      {darfAendern && (
        <div className="knopfreihe">
          <button
            type="button"
            className="knopf"
            onClick={() => {
              setzeNeu(true);
            }}
          >
            Wiedervorlage anlegen
          </button>
        </div>
      )}
      {neu && (
        <Wiedervorlageformular
          kunde={kunde}
          ich={ich}
          beiSchliessen={() => {
            setzeNeu(false);
          }}
          beiGespeichert={() => {
            setzeNeu(false);
            void holen();
          }}
        />
      )}
      {offen !== null && (
        <Wiedervorlagefenster
          id={offen}
          ich={ich}
          darfAendern={darfAendern}
          beiSchliessen={() => {
            setzeOffen(null);
          }}
          beiAenderung={() => void holen()}
        />
      )}
    </>
  );
}

/* ========================================================================== */
/* DAS FENSTER: ANSEHEN, ERLEDIGEN, WIEDER ÖFFNEN                              */
/* ========================================================================== */

type Ganz = Wiedervorlage & { readonly verlauf: readonly Verlaufseintrag[] };

function Wiedervorlagefenster({
  id,
  ich,
  darfAendern,
  beiSchliessen,
  beiAenderung,
}: {
  readonly id: string;
  readonly ich: string;
  readonly darfAendern: boolean;
  readonly beiSchliessen: () => void;
  readonly beiAenderung: () => void;
}): JSX.Element {
  const [daten, setzeDaten] = useState<Ganz | null>(null);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [vermerk, setzeVermerk] = useState('');
  const [bearbeiten, setzeBearbeiten] = useState(false);
  const [laeuft, setzeLaeuft] = useState(false);

  const holen = useCallback(async () => {
    try {
      setzeDaten(await ladeWiedervorlage(id));
    } catch (f) {
      if (!(f instanceof ApiFehler)) throw f;
      setzeFehler(f);
    }
  }, [id]);

  useEffect(() => {
    void holen();
  }, [holen]);

  const handeln = async (aufruf: () => Promise<void>): Promise<void> => {
    setzeLaeuft(true);
    try {
      await aufruf();
      setzeFehler(null);
      setzeVermerk('');
      await holen();
      beiAenderung();
    } catch (f) {
      if (!(f instanceof ApiFehler)) throw f;
      setzeFehler(f);
    } finally {
      setzeLaeuft(false);
    }
  };

  if (bearbeiten && daten !== null) {
    return (
      <Wiedervorlageformular
        vorhanden={daten}
        kunde={daten.kunde?.id ?? null}
        ich={ich}
        beiSchliessen={() => {
          setzeBearbeiten(false);
        }}
        beiGespeichert={() => {
          setzeBearbeiten(false);
          void holen();
          beiAenderung();
        }}
      />
    );
  }

  return (
    <Dialog
      titel={daten?.betreff ?? 'Wiedervorlage'}
      unterzeile={daten === null ? undefined : [daten.kunde?.name, bezugName(daten)].filter(Boolean).join(' · ')}
      breite="mittel"
      beiSchliessen={beiSchliessen}
    >
      <>
        {fehler !== null && <Meldung fehler={fehler} gezeigteFelder={['vermerk']} />}
        {daten === null && fehler === null && <p role="status">Wird geladen …</p>}
        {daten !== null && (
          <>
            <dl className="angaben">
              <dt>Fällig am</dt>
              <dd>
                {alsTag(daten.faelligAm)} {faelligkeitsmarke(daten)}
              </dd>
              <dt>Zuständig</dt>
              <dd>{zustaendigName(daten)}</dd>
              <dt>Priorität</dt>
              <dd>{PRIORITAETSNAME[daten.prioritaet]}</dd>
              {daten.erinnerungAm !== null && (
                <>
                  <dt>Erinnerung</dt>
                  <dd>{alsZeitpunkt(daten.erinnerungAm)}</dd>
                </>
              )}
              {daten.beschreibung !== null && (
                <>
                  <dt>Beschreibung</dt>
                  <dd className="wiedervorlage__text">{daten.beschreibung}</dd>
                </>
              )}
              {daten.erledigtAm !== null && (
                <>
                  <dt>Erledigt</dt>
                  <dd>
                    {alsZeitpunkt(daten.erledigtAm)}
                    {daten.erledigtVon !== null && ` von ${daten.erledigtVon}`}
                    {daten.erledigtVermerk !== null && (
                      <>
                        <br />
                        {daten.erledigtVermerk}
                      </>
                    )}
                  </dd>
                </>
              )}
            </dl>

            {daten.erledigtAm === null && daten.darfErledigen && (
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="wv-vermerk">
                  Erledigungsvermerk
                </label>
                <textarea
                  id="wv-vermerk"
                  className="feld__eingabe"
                  rows={2}
                  maxLength={2000}
                  value={vermerk}
                  onChange={(e) => {
                    setzeVermerk(e.target.value);
                  }}
                />
                <span className="feld__hilfe">Freiwillig — was herauskam.</span>
              </div>
            )}

            <section aria-label="Verlauf der Wiedervorlage">
              <h3 className="feld__beschriftung">Verlauf</h3>
              <ul className="wiedervorlage__verlauf">
                {daten.verlauf.map((v, i) => (
                  <li key={`${v.zeitpunkt}-${String(i)}`}>
                    <span className="zahl">{alsZeitpunkt(v.zeitpunkt)}</span> · {VERLAUFSNAME[v.art]}
                    {v.benutzer !== null && ` von ${v.benutzer}`}
                    {v.angabe !== null && ` — ${v.angabe}`}
                  </li>
                ))}
              </ul>
            </section>

            <Abschlussreihe
              abbruch={
                <button type="button" className="knopf" onClick={beiSchliessen}>
                  Schließen
                </button>
              }
            >
              {darfAendern && daten.erledigtAm === null && (
                <button
                  type="button"
                  className="knopf"
                  disabled={laeuft}
                  onClick={() => {
                    setzeBearbeiten(true);
                  }}
                >
                  Ändern
                </button>
              )}
              {daten.darfErledigen && daten.erledigtAm !== null && (
                <button
                  type="button"
                  className="knopf"
                  disabled={laeuft}
                  onClick={() => void handeln(() => wiedervorlageWiedereroeffnen(id))}
                >
                  Wieder öffnen
                </button>
              )}
              {daten.darfErledigen && daten.erledigtAm === null && (
                <button
                  type="button"
                  className="knopf knopf--haupt"
                  disabled={laeuft}
                  onClick={() =>
                    void handeln(() => wiedervorlageErledigen(id, vermerk.trim() === '' ? null : vermerk.trim()))
                  }
                >
                  Erledigt
                </button>
              )}
            </Abschlussreihe>
          </>
        )}
      </>
    </Dialog>
  );
}

/* ========================================================================== */
/* ANLEGEN UND ÄNDERN                                                          */
/* ========================================================================== */

export function Wiedervorlageformular({
  vorhanden,
  kunde,
  ich,
  beiSchliessen,
  beiGespeichert,
}: {
  readonly vorhanden?: Wiedervorlage | undefined;
  readonly kunde: string | null;
  readonly ich: string;
  readonly beiSchliessen: () => void;
  readonly beiGespeichert: () => void;
}): JSX.Element {
  const [auswahl, setzeAuswahl] = useState<Zustaendigenauswahl | null>(null);
  const [betreff, setzeBetreff] = useState(vorhanden?.betreff ?? '');
  const [beschreibung, setzeBeschreibung] = useState(vorhanden?.beschreibung ?? '');
  const [faelligAm, setzeFaelligAm] = useState(vorhanden?.faelligAm ?? heute());
  const [prioritaet, setzePrioritaet] = useState<Prioritaet>(vorhanden?.prioritaet ?? 2);
  const [erinnerung, setzeErinnerung] = useState(erinnerungAlsEingabe(vorhanden?.erinnerungAm ?? null));
  const [zustaendig, setzeZustaendig] = useState(() => {
    if (vorhanden === undefined) return wunschAlsWert({ art: 'person', id: ich });
    const z = vorhanden.zustaendig;
    return z.art === 'person' ? wunschAlsWert({ art: 'person', id: z.id }) : wunschAlsWert(z);
  });
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);

  useEffect(() => {
    ladeZustaendige().then(setzeAuswahl, (f: unknown) => {
      if (!(f instanceof ApiFehler)) throw f;
      setzeFehler(f);
    });
  }, []);

  const speichern = async (): Promise<void> => {
    const wunsch = wertAlsWunsch(zustaendig);
    if (wunsch === null) return;
    const eingabe = {
      betreff: betreff.trim(),
      beschreibung: beschreibung.trim() === '' ? null : beschreibung.trim(),
      faelligAm,
      prioritaet,
      erinnerungAm: erinnerungAlsIso(erinnerung),
      zustaendig: wunsch,
    };
    setzeLaeuft(true);
    try {
      if (vorhanden === undefined) await wiedervorlageAnlegen({ ...eingabe, kunde, bezug: null });
      else await wiedervorlageAendern(vorhanden.id, eingabe);
      beiGespeichert();
    } catch (f) {
      if (!(f instanceof ApiFehler)) throw f;
      setzeFehler(f);
    } finally {
      setzeLaeuft(false);
    }
  };

  const amFeld = (feld: string): readonly ApiFehler['verstoesse'][number][] => fehler?.alleZumFeld(feld) ?? [];

  return (
    <Dialog
      titel={vorhanden === undefined ? 'Wiedervorlage anlegen' : 'Wiedervorlage ändern'}
      unterzeile="Wann, von wem und was wieder vorgelegt wird."
      breite="mittel"
      beiSchliessen={beiSchliessen}
    >
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void speichern();
        }}
      >
        {fehler !== null && (
          <Meldung fehler={fehler} gezeigteFelder={['betreff', 'faelligAm', 'zustaendig', 'erinnerungAm']} />
        )}
        <Feld beschriftung="Betreff" wert={betreff} aendern={setzeBetreff} pflicht verstoesse={amFeld('betreff')} />
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="wv-beschreibung">
            Beschreibung
          </label>
          <textarea
            id="wv-beschreibung"
            className="feld__eingabe"
            rows={3}
            maxLength={4000}
            value={beschreibung}
            onChange={(e) => {
              setzeBeschreibung(e.target.value);
            }}
          />
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="wv-faellig">
            Fällig am
          </label>
          <input
            id="wv-faellig"
            className="feld__eingabe"
            type="date"
            required
            value={faelligAm}
            onChange={(e) => {
              setzeFaelligAm(e.target.value);
            }}
          />
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="wv-zustaendig">
            Zuständig
          </label>
          {/* KUN-15: eine Person oder eine Rolle — wer in der Rolle ist, sieht sie in seiner Liste. */}
          <select
            id="wv-zustaendig"
            className="feld__eingabe"
            value={zustaendig}
            onChange={(e) => {
              setzeZustaendig(e.target.value);
            }}
          >
            {auswahl === null && <option value={zustaendig}>Wird geladen …</option>}
            {auswahl !== null && (
              <>
                <optgroup label="Personen">
                  {auswahl.personen.map((p) => (
                    <option key={p.id} value={`person:${p.id}`}>
                      {p.id === ich ? `${p.name} (ich)` : p.name}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Rollen">
                  {auswahl.rollen.map((r) => (
                    <option key={r.kennung} value={`rolle:${r.kennung}`}>
                      {r.bezeichnung}
                    </option>
                  ))}
                </optgroup>
              </>
            )}
          </select>
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="wv-prioritaet">
            Priorität
          </label>
          <select
            id="wv-prioritaet"
            className="feld__eingabe"
            value={String(prioritaet)}
            onChange={(e) => {
              setzePrioritaet(Number(e.target.value) as Prioritaet);
            }}
          >
            {([1, 2, 3] as const).map((p) => (
              <option key={p} value={String(p)}>
                {PRIORITAETSNAME[p]}
              </option>
            ))}
          </select>
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="wv-erinnerung">
            Erinnerung
          </label>
          <input
            id="wv-erinnerung"
            className="feld__eingabe"
            type="datetime-local"
            value={erinnerung}
            onChange={(e) => {
              setzeErinnerung(e.target.value);
            }}
          />
          <span className="feld__hilfe">
            Freiwillig. Ab diesem Zeitpunkt steht sie unter „fällig" und auf der Startseite.
          </span>
        </div>
        <Abschlussreihe
          abbruch={
            <button type="button" className="knopf" onClick={beiSchliessen}>
              Abbrechen
            </button>
          }
        >
          <button type="submit" className="knopf knopf--haupt" disabled={laeuft || betreff.trim() === ''}>
            Speichern
          </button>
        </Abschlussreihe>
      </form>
    </Dialog>
  );
}
